import React, { useState, useRef } from "react";
import Papa from "papaparse";
import { Upload, Download, X, Copy, Image, Check } from "lucide-react";
import toast from "react-hot-toast";
import { Timestamp } from "firebase/firestore";
import { calculateAge } from "../utils/common";
import Preview from "./preview";

export default function BulkUploadFighter({ onParseSuccess, errors, onImagesChange }) {
    const [csvData, setCsvData] = useState(null);
    const [uploadedImages, setUploadedImages] = useState({});
    const fileInputRef = useRef(null);

    // Generate CSV template
    const generateCSVTemplate = () => {
        const headers = [
            "name",
            "email",
            "phoneNumber",
            "nationality",
            "birthdate",
            "age",
            "gender",
            "weightClass",
            "height",
            "icOrPassport",
            "gymNameOrTeamName",
            "walkOutMusic",
            "igUsername",
            "emergencyContactNumber",
            "boxing_win",
            "boxing_loss",
            "boxing_draw",
            "muay_thai_win",
            "muay_thai_loss",
            "muay_thai_draw",
            "kickboxing_win",
            "kickboxing_loss",
            "kickboxing_draw",
            "mma_win",
            "mma_loss",
            "mma_draw",
        ];

        const exampleRow = [
            "Ahmad bin Ali",
            "ahmad@example.com",
            "+60123456789",
            "Malaysian",
            "1995-01-15",
            "29",
            "M",
            "F",
            "175",
            "950115-01-1234",
            "Elite Gym",
            "Eye of the Tiger - Survivor",
            "@ahmadfighter",
            '+60123456780',
            "5",
            "2",
            "1",
            "3",
            "1",
            "0",
            "2",
            "0",
            "0",
            "1",
            "1",
            "0",
        ];

        const csvContent = [
            // "# Malaysia Fight League - Fighter Account CSV Template",
            // "# Instructions:",
            // "# 1. Fill in all required fields (marked with *)",
            // "# 2. Date format: YYYY-MM-DD",
            // "# 3. Phone format: +60123456789 (E.164)",
            // "# 4. Gender: M or F",
            // "# 5. Weight Class: A-K (see weight class guide)",
            // "# 6. Records: Enter numeric values only",
            // "#",
            headers.join(","),
            exampleRow.join(","),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "fighter_account_template.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("CSV template downloaded");
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith(".csv")) {
            toast.error("Please upload a CSV file");
            return;
        }

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                // Filter out comment rows
                const data = results.data.filter(
                    (row) => !row.name?.startsWith("#")
                );

                if (data.length === 0) {
                    toast.error("No valid data found in CSV file");
                    return;
                }

                // Validate and transform data
                const transformedData = [];
                const validationErrors = [];

                data.forEach((row, index) => {
                    const errors = validateRow(row, index + 1);
                    if (errors.length > 0) {
                        validationErrors.push({
                            row: index + 1,
                            email: row.email || "unknown",
                            errors: errors,
                        });
                    } else {
                        transformedData.push(transformRow(row));
                    }
                });

                if (validationErrors.length > 0) {
                    const errorMessages = validationErrors
                        .map(
                            (err) =>
                                `Row ${err.row} (${err.email}): ${err.errors.join(", ")}`
                        )
                        .join("\n");
                    toast.error(
                        `Validation errors found:\n${errorMessages}`,
                        { duration: 5000 }
                    );
                }

                if (transformedData.length > 0) {
                    setCsvData(transformedData);
                    setUploadedImages({}); // Reset uploaded images when new CSV is parsed
                    onParseSuccess(transformedData);
                    toast.success(
                        `Successfully parsed ${transformedData.length} fighter(s)`
                    );
                }
            },
            error: (error) => {
                toast.error(`Error parsing CSV: ${error.message}`);
            },
        });
    };

    const validateRow = (row, rowNumber) => {
        const errors = [];

        // Required fields
        if (!row.name || row.name.trim() === "") {
            errors.push("Name is required");
        }
        if (!row.email || row.email.trim() === "") {
            errors.push("Email is required");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push("Invalid email format");
        }
        if (!row.phoneNumber || row.phoneNumber.trim() === "") {
            errors.push("Phone number is required");
        }
        // else if (!/^\+[1-9]\d{9,14}$/.test(row.phoneNumber.trim())) {
        //     errors.push("Invalid phone format (use E.164: +60123456789)");
        // }

        // Optional but validate format if provided
        if (row.gender && !["M", "F"].includes(row.gender.toUpperCase())) {
            errors.push("Gender must be M or F");
        }
        if (
            row.weightClass &&
            !["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"].includes(
                row.weightClass.toUpperCase()
            )
        ) {
            errors.push("Invalid weight class (must be A-K)");
        }
        if (row.birthdate) {
            const date = new Date(row.birthdate);
            if (isNaN(date.getTime())) {
                errors.push("Invalid birthdate format (use YYYY-MM-DD)");
            }
        }

        return errors;
    };

    const transformRow = (row) => {
        const records = {
            BOXING: {
                win: parseInt(row.boxing_win) || 0,
                loss: parseInt(row.boxing_loss) || 0,
                draw: parseInt(row.boxing_draw) || 0,
            },
            MUAY_THAI: {
                win: parseInt(row.muay_thai_win) || 0,
                loss: parseInt(row.muay_thai_loss) || 0,
                draw: parseInt(row.muay_thai_draw) || 0,
            },
            KICKBOXING: {
                win: parseInt(row.kickboxing_win) || 0,
                loss: parseInt(row.kickboxing_loss) || 0,
                draw: parseInt(row.kickboxing_draw) || 0,
            },
            MMA: {
                win: parseInt(row.mma_win) || 0,
                loss: parseInt(row.mma_loss) || 0,
                draw: parseInt(row.mma_draw) || 0,
            },
        };

        let birthdate = null;
        let age = null;
        if (row.birthdate) {
            const date = new Date(row.birthdate);
            if (!isNaN(date.getTime())) {
                birthdate = Timestamp.fromDate(date);
                age = calculateAge(date);
            }
        } else if (row.age) {
            age = parseInt(row.age);
        }

        return {
            name: row.name.trim(),
            email: row.email.trim().toLowerCase(),
            phoneNumber: row.phoneNumber.trim(),
            nationality: row.nationality || "Malaysian",
            birthdate: birthdate,
            age: age,
            gender: row.gender?.toUpperCase() || null,
            weightClass: row.weightClass?.toUpperCase() || null,
            height: row.height ? parseInt(row.height) : null,
            icOrPassport: row.icOrPassport || null,
            gymNameOrTeamName: row.gymNameOrTeamName || null,
            walkOutMusic: row.walkOutMusic || null,
            igUsername: row.igUsername || null,
            emergencyContactNumber: row.emergencyContactNumber || null,
            records: records,
        };
    };

    const clearFile = () => {
        setCsvData(null);
        setUploadedImages({});
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onParseSuccess([]);
        if (onImagesChange) {
            onImagesChange({});
        }
    };

    const handleImageUpload = (index, file) => {
        if (!file) return;

        // Validate file
        const fileName = file.name;
        const fileExt = fileName.split(".").pop().toLowerCase();
        const allowedExts = ["jpg", "jpeg", "png", "webp"];

        if (!allowedExts.includes(fileExt)) {
            toast.error(
                `Invalid image type. Allowed: ${allowedExts.join(", ")}`
            );
            return;
        }

        const maxSize = 25 * 1024 * 1024; // 25MB
        if (file.size > maxSize) {
            toast.error("File size too large (max 25MB)");
            return;
        }

        // Update uploaded images state
        const newUploadedImages = {
            ...uploadedImages,
            [index]: file,
        };
        setUploadedImages(newUploadedImages);

        // Notify parent component
        if (onImagesChange) {
            onImagesChange(newUploadedImages);
        }
    };

    const removeImage = (index) => {
        const newUploadedImages = { ...uploadedImages };
        delete newUploadedImages[index];
        setUploadedImages(newUploadedImages);

        // Notify parent component
        if (onImagesChange) {
            onImagesChange(newUploadedImages);
        }
    };

    const allImagesUploaded = csvData && csvData.length > 0 && csvData.every((_, index) => uploadedImages[index]);

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        if (timestamp.toDate) {
            return timestamp.toDate().toISOString().split("T")[0];
        }
        return "-";
    };

    // Generate password preview (same logic as cloud function)
    const generatePasswordPreview = (name, phoneNumber) => {
        if (!name || !phoneNumber) return "-";
        const firstName = name.split(" ")[0];
        const phoneLast4 = phoneNumber.replace(/[^0-9]/g, "").slice(-4);
        if (phoneLast4.length < 4) return "-";
        return `${firstName}${phoneLast4}!`;
    };

    return (
        <div className="space-y-4">
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    BULK UPLOAD (CSV)
                </h3>

                <div className="space-y-4">
                    <div className="flex gap-2">
                        <button
                            className="btn btn-outline"
                            onClick={generateCSVTemplate}
                        >
                            <Download size={18} className="mr-2" />
                            Download Template
                        </button>
                    </div>

                    <div className="border-2 border-dashed border-base-content p-8 text-center">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="csv-upload"
                        />
                        <label
                            htmlFor="csv-upload"
                            className="cursor-pointer flex flex-col items-center gap-4"
                        >
                            <Upload size={48} />
                            <div>
                                <span className="font-bold">
                                    Click to upload CSV file
                                </span>
                                <p className="text-sm text-base-content/70">
                                    or drag and drop
                                </p>
                            </div>
                        </label>
                    </div>

                    {csvData && csvData.length > 0 && (
                        <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                                <div className="font-bold">
                                    ✓ Parsed {csvData.length} fighter(s)
                                    {csvData.length > 0 && (
                                        <span className="ml-2 text-sm font-normal">
                                            ({Object.keys(uploadedImages).length}/{csvData.length} images uploaded)
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={clearFile}
                                >
                                    <X size={16} className="mr-1" />
                                    Clear
                                </button>
                            </div>
                            {!allImagesUploaded && (
                                <div className="alert alert-warning mb-2">
                                    <span>
                                        Please upload profile pictures for all fighters before submitting.
                                        ({Object.keys(uploadedImages).length}/{csvData.length} uploaded)
                                    </span>
                                </div>
                            )}

                            <div className="overflow-x-auto border-2 border-base-content">
                                <table className="table table-bordered table-sm w-full">
                                    <thead className="bg-base-200">
                                        <tr>
                                            <th className="sticky left-0 bg-base-200 z-10">#</th>
                                            <th className="sticky left-[3rem] bg-base-200 z-10">Profile Picture</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone Number</th>
                                            <th>Password Preview</th>
                                            <th>Nationality</th>
                                            <th>Birthdate</th>
                                            <th>Age</th>
                                            <th>Gender</th>
                                            <th>Weight Class</th>
                                            <th>Height</th>
                                            <th>IC/Passport</th>
                                            <th>Gym/Team Name</th>
                                            <th>Walk Out Music</th>
                                            <th>IG Username</th>
                                            <th>Emergency Contact</th>
                                            <th>Boxing [W]</th>
                                            <th>Boxing [L]</th>
                                            <th>Boxing [D]</th>
                                            <th>Muay Thai [W]</th>
                                            <th>Muay Thai [L]</th>
                                            <th>Muay Thai [D]</th>
                                            <th>Kickboxing [W]</th>
                                            <th>Kickboxing [L]</th>
                                            <th>Kickboxing [D]</th>
                                            <th>MMA [W]</th>
                                            <th>MMA [L]</th>
                                            <th>MMA [D]</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {csvData.map((row, index) => {
                                            const passwordPreview = generatePasswordPreview(row.name, row.phoneNumber);
                                            const hasImage = uploadedImages[index];
                                            return (
                                            <tr key={index}>
                                                <td className="sticky left-0 bg-base-100 z-10 font-bold">{index + 1}</td>
                                                <td className="sticky left-[3rem] bg-base-100 z-10">
                                                    <div className="flex flex-col items-center gap-2 min-w-[120px]">
                                                        {hasImage ? (
                                                            <>
                                                                <div className="w-16 h-16 border-2 border-base-content overflow-hidden rounded">
                                                                    <Preview
                                                                        src={hasImage}
                                                                        className="w-full h-full"
                                                                        imgClassName="object-cover"
                                                                    />
                                                                </div>
                                                                <div className="flex gap-1">
                                                                    <button
                                                                        className="btn btn-xs btn-success"
                                                                        title="Image uploaded"
                                                                        disabled
                                                                    >
                                                                        <Check size={12} />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-xs btn-error"
                                                                        onClick={() => removeImage(index)}
                                                                        title="Remove image"
                                                                    >
                                                                        <X size={12} />
                                                                    </button>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <label className="btn btn-xs btn-outline cursor-pointer">
                                                                <Image size={14} className="mr-1" />
                                                                Upload
                                                                <input
                                                                    type="file"
                                                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                                                    className="hidden"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            handleImageUpload(index, file);
                                                                        }
                                                                        e.target.value = ""; // Reset input
                                                                    }}
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap">{row.name || "-"}</td>
                                                <td className="whitespace-nowrap">{row.email || "-"}</td>
                                                <td className="whitespace-nowrap">{row.phoneNumber || "-"}</td>
                                                <td className="whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <code className="bg-base-200 px-2 py-1 rounded font-mono text-xs">
                                                            {passwordPreview}
                                                        </code>
                                                        {passwordPreview !== "-" && (
                                                            <button
                                                                className="btn btn-xs btn-ghost"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(passwordPreview);
                                                                    toast.success(`Copied password for ${row.name}`);
                                                                }}
                                                                title="Copy password"
                                                            >
                                                                <Copy size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap">{row.nationality || "-"}</td>
                                                <td className="whitespace-nowrap">{formatDate(row.birthdate)}</td>
                                                <td className="text-center">{row.age || "-"}</td>
                                                <td className="text-center">{row.gender || "-"}</td>
                                                <td className="text-center">{row.weightClass || "-"}</td>
                                                <td className="text-center">{row.height || "-"}</td>
                                                <td className="whitespace-nowrap">{row.icOrPassport || "-"}</td>
                                                <td className="whitespace-nowrap">{row.gymNameOrTeamName || "-"}</td>
                                                <td className="whitespace-nowrap">{row.walkOutMusic || "-"}</td>
                                                <td className="whitespace-nowrap">{row.igUsername || "-"}</td>
                                                <td className="whitespace-nowrap">{row.emergencyContactNumber || "-"}</td>
                                                <td className="text-center">{row.records?.BOXING?.win || 0}</td>
                                                <td className="text-center">{row.records?.BOXING?.loss || 0}</td>
                                                <td className="text-center">{row.records?.BOXING?.draw || 0}</td>
                                                <td className="text-center">{row.records?.MUAY_THAI?.win || 0}</td>
                                                <td className="text-center">{row.records?.MUAY_THAI?.loss || 0}</td>
                                                <td className="text-center">{row.records?.MUAY_THAI?.draw || 0}</td>
                                                <td className="text-center">{row.records?.KICKBOXING?.win || 0}</td>
                                                <td className="text-center">{row.records?.KICKBOXING?.loss || 0}</td>
                                                <td className="text-center">{row.records?.KICKBOXING?.draw || 0}</td>
                                                <td className="text-center">{row.records?.MMA?.win || 0}</td>
                                                <td className="text-center">{row.records?.MMA?.loss || 0}</td>
                                                <td className="text-center">{row.records?.MMA?.draw || 0}</td>
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {errors && errors.length > 0 && (
                        <div className="alert alert-error">
                            <div>
                                <h3 className="font-bold">Validation Errors:</h3>
                                <ul className="list-disc list-inside">
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
